import { useEffect } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { useTooltip } from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../../hooks';
import { generateTrackingId } from '../../utils';
import { PatientTooltip } from '../tooltip/patient-tooltip';
import { CallIntelChip } from './call-intel-chip';

type Props = {
  schedulingOutcome: CallIntelTypes.SchedulingOutcomeV2Enum;
  disabled?: boolean;
  trackingContext?: string;
} & (
  | {
      call: CallIntelTypes.Call;
      showTooltip: true;
    }
  | {
      call?: CallIntelTypes.Call;
      showTooltip?: false;
    }
);

export const SchedulingChip = ({ schedulingOutcome, call, disabled, showTooltip = true, trackingContext }: Props) => {
  const { tooltipProps, triggerProps, Tooltip, isOpen } = useTooltip({
    placement: 'right',
    showArrow: false,
    theme: 'light',
  });
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  const hasAppointments = !!call?.appointmentMetadata?.length;
  const isRecoveredOpportunity =
    schedulingOutcome === CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL;
  const shouldShowTooltip = showTooltip && isRecoveredOpportunity && isOpen && hasAppointments;

  const disabledProps = disabled
    ? {
        opacity: 0.5,
        strikeThrough: true,
        strikeThroughConfig: { color: theme.colors.warning80 },
      }
    : {};

  useEffect(() => {
    if (shouldShowTooltip) {
      pendo.track(generateTrackingId({ component: 'patient-tooltip', context: 'hover' }), {
        context: trackingContext ?? '',
      });
    }
  }, [shouldShowTooltip, trackingContext]);

  return (
    <div
      {...triggerProps}
      css={css`
        max-width: 100%;
      `}
    >
      <CallIntelChip
        as='schedulingOutcome'
        chipVariants={chipVariants}
        dataKey={schedulingOutcome}
        label={dataLabels.schedulingOutcomeV2?.[schedulingOutcome] || schedulingOutcome}
        key={schedulingOutcome}
        css={isRecoveredOpportunity && hasAppointments ? styles.recoveredOpportunityChip : undefined}
        {...disabledProps}
        hideTooltip
      />
      {shouldShowTooltip && (
        <Tooltip {...tooltipProps} css={styles.tooltip}>
          <PatientTooltip call={call} />
        </Tooltip>
      )}
    </div>
  );
};

const styles = {
  recoveredOpportunityChip: css`
    border: 1px solid ${theme.colors.secondary.seaweed70};
    border-style: dashed;
  `,
  tooltip: css`
    padding: 0;
    font-weight: normal;
    border-radius: ${theme.borderRadius.large};
    background-color: ${theme.colors.neutral5};
  `,
};
